import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { colors } from '../utils/theme';
import { 
  Activity, Map as MapIcon, Clock, Filter, 
  Calendar, ChevronRight, X, Search 
} from 'lucide-react';
import Swal from 'sweetalert2';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  
  // Filters State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);


  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Logic to get current items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSessions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDate, selectedMonth]);

  const GOOGLE_MAP_KEY = "AIzaSyAmGcPTfePx1WqX-8bqlPno6-5hU_GE9uA";


 const formatIST = (utcString) => {
  if (!utcString) return null;
  const date = new Date(utcString);
  
  // Format to IST dd-mm-yyyy HH:mm:ss
  const istString = date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // Use true for AM/PM format
  });

  // istString looks like "09/05/2026, 12:52:57 pm"
  const [datePart, timePart] = istString.split(", ");
  return {
    date: datePart.replace(/\//g, "-"), // converts 09/05/2026 to 09-05-2026
    time: timePart.toUpperCase()       // 12:52:57 PM
  };
};

  // 1. Fix Map Loading (useJsApiLoader)
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAP_KEY
  });

  useEffect(() => {
    fetchSessions();
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get("https://gpsapi.emedha.in/web/api/sessions");
      setSessions(res.data);
      setFilteredSessions(res.data);
    } catch (e) { console.error(e); }
  };

  // 2. Comprehensive Filter Logic (Search + Month + Day)
  useEffect(() => {
    let data = [...sessions];

    if (searchTerm) {
      data = data.filter(s => s.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    if (selectedDate) {
      data = data.filter(s => s.start_time.startsWith(selectedDate));
    }

    if (selectedMonth) {
      // Expects start_time format "YYYY-MM-DD"
      data = data.filter(s => s.start_time.split('-')[1] === selectedMonth);
    }

    setFilteredSessions(data);
  }, [searchTerm, selectedDate, selectedMonth, sessions]);

  const isMobile = windowWidth < 1024;

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.mainContainer}>
        
        {/* Header & Advanced Filters */}
        <header style={styles.header}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <h1 style={{ ...styles.title, fontSize: isMobile ? '24px' : '32px' }}>Session Intelligence</h1>
            <p style={styles.subtitle}>Audit employee movement and shift timelines</p>
          </div>

          <div style={styles.filterBar}>
            {/* Search */}
            <div style={styles.inputWrapper}>
              <Search size={16} color="#999" />
              <input 
                placeholder="Staff name..." 
                style={styles.input} 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Date Picker */}
            <div style={styles.inputWrapper}>
              <Calendar size={16} color="#999" />
              <input 
                type="date" 
                style={styles.input} 
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            {/* Month Selector */}
            <select 
              style={styles.select} 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">All Months</option>
              {["01","02","03","04","05","06","07","08","09","10","11","12"].map(m => (
                <option key={m} value={m}>{new Date(2026, m-1).toLocaleString('default', { month: 'long' })}</option>
              ))}
            </select>

            {(searchTerm || selectedDate || selectedMonth) && (
              <button onClick={() => {setSearchTerm(""); setSelectedDate(""); setSelectedMonth("");}} style={styles.clearBtn}>
                <X size={14} />
              </button>
            )}
          </div>
        </header>

        {/* Data Table */}
        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Department</th>
                  <th style={styles.th}>Shift Start</th>
                  <th style={styles.th}>Shift End</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Geo-Location</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((session) => (
                  <tr key={session.id} style={styles.tr}>
                    {/* Employee Info */}
                    <td style={styles.td}>
                      <div style={styles.empInfo}>
                        <div style={styles.avatar}>{session.employee_name?.[0]}</div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={styles.empName}>{session.employee_name}</div>
                          <div style={styles.empSub}>{session.mobile}</div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Dept */}
                    <td style={styles.td}>
                      <span style={styles.deptBadge}>{session.department_id}</span>
                    </td>

                    {/* Start Time */}
{/* Start Time */}
<td style={styles.td}>
  {(() => {
    const ist = formatIST(session.start_time);
    return ist ? (
      <div style={styles.timeLabel}>
        <div style={{ fontWeight: '700', color: '#333' }}>{ist.date}</div>
        <div style={{ fontSize: '12px', color: colors.primary, fontWeight: '600' }}>{ist.time}</div>
      </div>
    ) : <span>---</span>;
  })()}
</td>

{/* End Time */}
<td style={styles.td}>
  {(() => {
    const ist = formatIST(session.end_time);
    return ist ? (
      <div style={styles.timeLabel}>
        <div style={{ fontWeight: '700', color: '#333' }}>{ist.date}</div>
        <div style={{ fontSize: '12px', color: '#666' }}>{ist.time}</div>
      </div>
    ) : <span style={{ color: '#ccc', fontStyle: 'italic', fontSize: '13px' }}>Ongoing Session</span>;
  })()}
</td>

                    {/* Status */}
                    <td style={styles.td}>
                       <span style={{
                         ...styles.statusBadge,
                         backgroundColor: session.status === 'ACTIVE' ? '#e6fffa' : '#f5f5f5',
                         color: session.status === 'ACTIVE' ? '#319795' : '#999'
                       }}>
                         {session.status}
                       </span>
                    </td>

                    {/* Map Action */}
                    <td style={styles.td}>
                      <button 
                        onClick={() => setSelectedLocation({ 
                          lat: parseFloat(session.start_latitude), 
                          lng: parseFloat(session.start_longitude) 
                        })}
                        style={styles.mapBtn}
                      >
                        <MapIcon size={14} color={colors.primary} />
                        <span>Show Map</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.paginationFooter}>
              <div style={styles.paginationInfo}>
                Showing <span style={{ fontWeight: '700' }}>{indexOfFirstItem + 1}</span> to <span style={{ fontWeight: '700' }}>{Math.min(indexOfLastItem, filteredSessions.length)}</span> of <span style={{ fontWeight: '700' }}>{filteredSessions.length}</span> sessions
              </div>
              
              <div style={styles.paginationControls}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '20px' }}>
                   <span style={{ fontSize: '12px', color: '#999', fontWeight: '600' }}>Per Page:</span>
                   <select 
                    value={itemsPerPage} 
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    style={styles.pageSelect}
                   >
                     {[10, 25, 50].map(val => <option key={val} value={val}>{val}</option>)}
                   </select>
                </div>

                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  style={{ ...styles.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}
                >
                  Prev
                </button>
                
                <div style={styles.pageNum}>
                  Page <span style={{ color: colors.primary }}>{currentPage}</span> of {totalPages || 1}
                </div>

                <button 
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  style={{ ...styles.pageBtn, opacity: (currentPage === totalPages || totalPages === 0) ? 0.4 : 1 }}
                >
                  Next
                </button>
              </div>
            </div>
   
          </div>
        </div>
      </div>

      {/* Map Modal */}
      {selectedLocation && (
        <div style={styles.modalOverlay} onClick={() => setSelectedLocation(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontWeight: '800' }}>Verification Point</h3>
              <X size={20} cursor="pointer" onClick={() => setSelectedLocation(null)} />
            </div>
            <div style={styles.mapContainer}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={selectedLocation}
                  zoom={16}
                >
                  <Marker position={selectedLocation} />
                </GoogleMap>
              ) : <div style={styles.loader}>Loading Map Intelligence...</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  pageWrapper: { backgroundColor: colors.background, minHeight: '100vh', display: 'flex', justifyContent: 'center' },
  mainContainer: { width: '100%', maxWidth: '1300px', padding: '30px 20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', flexWrap: 'wrap', gap: 20 },
  title: { fontWeight: '900', color: colors.text, margin: 0, letterSpacing: '-1px' },
  subtitle: { color: '#888', marginTop: 5, fontSize: '14px' },
  
  // Filter Bar
  filterBar: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' },
  inputWrapper: { display: 'flex', alignItems: 'center', backgroundColor: '#fff', padding: '10px 15px', borderRadius: '12px', border: '1px solid #eee' },
  input: { border: 'none', outline: 'none', marginLeft: 8, fontSize: '13px', color: '#444' },
  select: { padding: '10px 15px', borderRadius: '12px', border: '1px solid #eee', backgroundColor: '#fff', fontSize: '13px', outline: 'none', cursor: 'pointer' },
  clearBtn: { backgroundColor: colors.primary, width: 35, height: 35, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', color: '#fff', cursor: 'pointer' },

  card: { backgroundColor: '#fff', borderRadius: '25px', boxShadow: '0 20px 50px rgba(0,0,0,0.03)', border: '1px solid #f1f1f1', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'center', padding: '20px', color: '#bbb', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #f7f7f7' },
  td: { padding: '20px', borderBottom: '1px solid #fbfbfb', textAlign: 'center' },
  
  empInfo: { display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' },
  avatar: { width: 38, height: 38, borderRadius: 12, backgroundColor: colors.primary + '15', color: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800' },
  empName: { fontWeight: '700', color: '#1A1A1A', fontSize: '14px' },
  empSub: { fontSize: '11px', color: '#aaa' },
  deptBadge: { padding: '5px 12px', backgroundColor: '#fdf9f7', border: '1px solid #eee', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#666' },
  timeLabel: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statusBadge: { padding: '6px 12px', borderRadius: '8px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase' },
  mapBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: '12px', border: '1px solid #eee', backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', transition: '0.2s' },

  // Modal
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' },
  modal: { width: '90%', maxWidth: '600px', backgroundColor: '#fff', borderRadius: '30px', padding: '30px', boxShadow: '0 30px 70px rgba(0,0,0,0.4)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' },
  mapContainer: { width: '100%', height: '400px', backgroundColor: '#f9f9f9', borderRadius: '20px', overflow: 'hidden' },
  loader: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontWeight: '600' },

paginationFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 30px',
    backgroundColor: '#fff',
    borderTop: '1px solid #f7f7f7',
    flexWrap: 'wrap',
    gap: '15px'
  },
  paginationInfo: {
    fontSize: '13px',
    color: '#777',
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  pageSelect: {
    padding: '5px 10px',
    borderRadius: '8px',
    border: '1px solid #eee',
    fontSize: '12px',
    outline: 'none',
    cursor: 'pointer',
    fontWeight: '700',
    color: '#444'
  },
  pageBtn: {
    padding: '8px 16px',
    borderRadius: '10px',
    border: '1px solid #eee',
    backgroundColor: '#fff',
    fontSize: '12px',
    fontWeight: '700',
    color: '#444',
    cursor: 'pointer',
    transition: '0.2s',
  },
  pageNum: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#444',
    padding: '0 10px'
  }

};

export default Sessions;